import {
    DependenciesPayload,
    OfferDisplayInfo,
} from '@sxm-de/dev-kits/web-sdk/types';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

export async function getOfferCmsDisplayName(
    { offerIds }: { offerIds: string[] },
    { contentfulSdk }: DependenciesPayload,
): Promise<OfferDisplayInfo> {
    try {
        const response = (await contentfulSdk?.getOffers({
            offerIds: offerIds,
            locale: '*',
            select: 'sys.id,fields.offer_id,fields.packages,fields.care_marketing_info',
        })) as any;

        if (Array.isArray(response) && response.length > 0) {
            const locales = new Set<string>();
            const data = offerIds.reduce((items, offerId) => {
                const { entry, keyName } = getEntryInfo(response);

                const displayNames: Record<string, { displayName: string }> =
                    Object.keys(entry.fields[keyName]).reduce((all, locale) => {
                        locales.add(locale);
                        return {
                            ...all,
                            [locale]: {
                                displayName: entry.fields[keyName][locale],
                            },
                        };
                    }, {});

                let offerAddons: Record<string, { products: any }> | null =
                    null;
                if (entry?.fields?.offer_addons) {
                    offerAddons = Object.keys(entry.fields.offer_addons).reduce(
                        (all, locale) => {
                            locales.add(locale);
                            return {
                                ...all,
                                [locale]: {
                                    products: getOfferProductsInfo(
                                        entry.fields.offer_addons[locale],
                                        locale,
                                    ),
                                },
                            };
                        },
                        {},
                    );
                }

                return {
                    ...items,
                    [offerId]: [...locales].reduce((all, locale) => {
                        return {
                            ...all,
                            [locale]: {
                                displayName:
                                    displayNames?.[locale]?.displayName,
                                products: offerAddons?.[locale]?.products || {},
                            },
                        };
                    }, {}),
                };
            }, {} as OfferDisplayInfo);

            return data;
        } else {
            return {} as OfferDisplayInfo;
        }
    } catch (error: any) {
        /**
         * TODO:
         * Log error to error logger (not analytics).
         */
        throw new Error('SYSTEM');
    }
}

const getEntryInfo = (response: any) => {
    const packagesEntry = response[0].fields?.packages?.['en-US'][0];
    const marketingInfoEntry =
        response[0].fields?.care_marketing_info?.['en-US'];

    const entry = marketingInfoEntry.fields?.offer_display_name
        ? marketingInfoEntry
        : packagesEntry;
    const keyName = marketingInfoEntry.fields?.offer_display_name
        ? 'offer_display_name'
        : 'package_display_name';

    return { entry, keyName };
};

const getOfferProductsInfo = (offerAddons: any, locale: string) => {
    let productInfo = {};
    offerAddons.forEach((addon: any) => {
        productInfo = {
            ...productInfo,
            ...toProductInfoFromOfferAddon(addon, locale),
        };
    });

    return productInfo;
};

const toProductInfoFromOfferAddon = (addon: any, locale: string) => {
    return {
        [addon.fields?.addon_id?.[locale]]: {
            name: addon.fields?.name?.[locale],
            title: addon.fields?.header?.[locale],
            subTitle: addon.fields?.addon_note_text?.[locale],
            description: convertDocumentToHtmlString(
                addon.fields?.description?.[locale],
            ),
            imageUrl: addon.fields?.product_image?.[locale]?.url,
            imageAltText: addon.fields?.product_image?.[locale]?.altText,
            redeemInfo: {
                title: addon.fields?.post_purchase_cta_module?.[locale]?.fields
                    ?.headline?.[locale],
                description: convertDocumentToHtmlString(
                    addon.fields?.post_purchase_cta_module?.[locale]?.fields
                        ?.description?.[locale],
                ),
                cta: {
                    buttonLabel:
                        addon.fields?.post_purchase_cta_module?.[locale]?.fields
                            ?.cta?.[locale]?.fields?.link_text?.[locale],
                    buttonAriaLabel:
                        addon.fields?.post_purchase_cta_module?.[locale]?.fields
                            ?.cta?.[locale]?.fields?.aria_label?.[locale],
                    href: addon.fields?.post_purchase_cta_module?.[locale]
                        ?.fields?.cta?.[locale]?.fields?.href?.[locale],
                },
            },
        },
    };
};

/**
 * TODO[ARCH]
 * Explore adding this as helper or method within the contentfulSDK.
 */
const convertDocumentToHtmlString = (value: any) => {
    return documentToHtmlString(value, { preserveWhitespace: true });
};
